import React, { useState, useEffect, lazy, Suspense } from "react";
import { formatDate, formatShortDate } from "../utils/dates";
import axios from "axios";
import { wellPhaseLabel } from "../utils/wellPhase";
import jsPDF from "jspdf";

import WeatherWidget from "../components/WeatherWidget";
import JsaSummary from "../components/JSA/JsaSummary";
import JsaModal from "../components/JSA/JsaModal";
import JsaHistoryDrawer from "../components/JSA/JsaHistoryDrawer";
import NewExpense from "./NewExpense";
import { canChangeExpense, canWriteJsa } from "../utils/roles";
import ErrorBanner from "../components/ErrorBanner";
import AfeCard from "../components/AfeCard";
import { downloadFile } from "../components/fieldUi";
import { showsTankGauging } from "../utils/orgType";
import { useTranslation } from "react-i18next";
import { LoadingLine, PageFallback } from "../components/Spinner";
import Pagination from "../components/Pagination";
import ScrollTabs from "../components/ScrollTabs";

import { getTodayJsa, getJsaHistory } from "../services/jsaService";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

const WellboreSchematic = lazy(() => import("./WellboreSchematic"));
const BuildSheet = lazy(() => import("./BuildSheet"));
const WellIntelligence = lazy(() => import("./WellIntelligence"));
const ProcedureAnalyzer = lazy(() => import("./ProcedureAnalyzer"));
const VendorDashboard = lazy(() => import("./VendorDashboard"));
const SafetyDashboard = lazy(() => import("./SafetyDashboard"));
const WellDocuments = lazy(() => import("./WellDocuments"));
const WellTanks = lazy(() => import("./WellTanks"));

// Shown next to the full tab label on a phone, so a tab is recognizable
// even when it's partly scrolled off the edge.
const TAB_ICONS = {
  overview: "📋",
  expenses: "💲",
  reports: "📝",
  safety: "🦺",
  vendor: "🚚",
  intelligence: "📈",
  schematic: "📐",
  buildsheet: "🔩",
  documents: "📁",
  tanks: "🛢",
};

// Mirrors the RISK_BADGE color/label mapping in LeaseManagement.js —
// duplicated rather than imported since LeaseManagement.js isn't otherwise
// a dependency of this page and pulling in a whole lazy-loaded page module
// just for two small constants isn't worth it.
const LEASE_BADGE_COLOR = {
  active_hbp: "#00cc66",
  active_term: "#00f5ff",
  expiring_soon: "#ffaa00",
  expired: "#ff4466",
};
const LEASE_BADGE_LABEL = {
  active_hbp: "LEASE: HBP",
  active_term: "LEASE: IN TERM",
  expiring_soon: "LEASE: EXPIRING SOON",
  expired: "LEASE: EXPIRED",
};

const CATEGORIES = [
  "Rig Day Rate",
  "Labor",
  "Fuel / Diesel",
  "Equipment Rental",
  "Chemicals / Mud",
  "Catering / Camp",
  "Transportation",
  "Safety Equipment",
  "Fishing / Remedial",
  "Cementing",
  "Wireline",
  "Trucking",
  "Water Disposal",
  "Miscellaneous",
  "Other",
];

// ─── RESPONSIVE HOOK ─────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── PDF EXPORT FUNCTION ─────────────────────────────────
function exportReportPDF(r, wellName) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 48;
  const colW = pageW - margin * 2;
  let y = margin;

  doc.setFillColor(2, 8, 23);
  doc.rect(0, 0, pageW, 70, "F");
  doc.setFontSize(22);
  doc.setTextColor(0, 245, 255);
  doc.setFont("helvetica", "bold");
  doc.text("RIGIQ", margin, 36);
  doc.setFontSize(9);
  doc.setTextColor(100, 180, 200);
  doc.setFont("helvetica", "normal");
  doc.text("FIELD OPTIMIZATION PLATFORM", margin, 52);
  doc.setFontSize(11);
  doc.setTextColor(0, 245, 255);
  doc.setFont("helvetica", "bold");
  doc.text("DAILY OPERATIONS REPORT", pageW - margin, 36, { align: "right" });
  doc.setFontSize(9);
  doc.setTextColor(100, 180, 200);
  doc.setFont("helvetica", "normal");
  doc.text(`Generated: ${new Date().toLocaleString()}`, pageW - margin, 52, {
    align: "right",
  });

  y = 90;
  doc.setDrawColor(0, 245, 255);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageW - margin, y);
  y += 16;

  doc.setFontSize(9);
  doc.setTextColor(0, 245, 255);
  doc.setFont("helvetica", "bold");
  doc.text("WELL", margin, y);
  doc.text("DATE", margin + colW / 3, y);
  doc.text("REPORT ID", margin + (colW / 3) * 2, y);
  y += 14;
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(wellName, margin, y);
  doc.text(
    formatDate(r.date),
    margin + colW / 3,
    y,
  );
  doc.text(
    `RPT-${r._id.toString().slice(-6).toUpperCase()}`,
    margin + (colW / 3) * 2,
    y,
  );
  y += 24;

  doc.setDrawColor(40, 60, 80);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 16;

  doc.setFontSize(9);
  doc.setTextColor(0, 245, 255);
  doc.setFont("helvetica", "bold");
  doc.text("DRILLING DATA", margin, y);
  y += 14;

  const drillingFields = [
    ["Footage Drilled", r.footage !== undefined ? `${r.footage} ft` : "—"],
    ["Mud Weight", r.mud_weight !== undefined ? `${r.mud_weight} ppg` : "—"],
    ["Bit Depth", r.bit_depth !== undefined ? `${r.bit_depth} ft` : "—"],
    [
      "Hours Drilling",
      r.hours_drilling !== undefined ? `${r.hours_drilling} hrs` : "—",
    ],
  ];
  drillingFields.forEach(([label, value], i) => {
    const x = margin + (i % 2) * (colW / 2);
    if (i % 2 === 0 && i > 0) y += 28;
    doc.setFontSize(8);
    doc.setTextColor(120, 160, 180);
    doc.setFont("helvetica", "normal");
    doc.text(label.toUpperCase(), x, y);
    doc.setFontSize(12);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.text(value, x, y + 14);
  });
  y += 42;

  doc.setDrawColor(40, 60, 80);
  doc.setLineWidth(0.3);
  doc.line(margin, y, pageW - margin, y);
  y += 16;

  if (r.operations) {
    doc.setFontSize(9);
    doc.setTextColor(0, 245, 255);
    doc.setFont("helvetica", "bold");
    doc.text("OPERATIONS NARRATIVE", margin, y);
    y += 14;
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "normal");
    const opLines = doc.splitTextToSize(r.operations, colW);
    opLines.forEach((line) => {
      if (y > 720) {
        doc.addPage();
        y = margin;
      }
      doc.text(line, margin, y);
      y += 14;
    });
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(0, 245, 255);
    doc.setLineWidth(0.3);
    doc.line(margin, 760, pageW - margin, 760);
    doc.setFontSize(8);
    doc.setTextColor(80, 120, 140);
    doc.setFont("helvetica", "normal");
    doc.text("RIGIQ Field Optimization Platform — Confidential", margin, 774);
    doc.text(`Page ${i} of ${pageCount}`, pageW - margin, 774, {
      align: "right",
    });
  }

  doc.save(
    `RIGIQ_Report_${wellName.replace(/\s+/g, "_")}_${new Date(r.date).toISOString().split("T")[0]}.pdf`,
  );
}

// ─── EXPENSE CARD ─────────────────────────────────────────
function ExpenseCard({ expense, user, token, onDeleted, onUpdated, isMobile }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === "es" ? "es-US" : "en-US";
  const [editing, setEditing] = useState(false);
  const [lineItems, setLineItems] = useState(expense.lineItems || []);
  const [notes, setNotes] = useState(expense.notes || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";
  const card = "rgba(0,245,255,0.03)";
  const total = lineItems.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );

  const inputStyle = {
    width: "100%",
    backgroundColor: "rgba(0,245,255,0.05)",
    color: "white",
    border: "1px solid rgba(0,245,255,0.2)",
    borderRadius: "4px",
    padding: "6px 10px",
    fontSize: "12px",
    outline: "none",
    boxSizing: "border-box",
  };

  function addItem() {
    setLineItems([
      ...lineItems,
      {
        description: "",
        amount: "",
        vendor: "",
        category: "",
        codeId: "",
        notes: "",
      },
    ]);
  }
  function removeItem(idx) {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  }
  function updateItem(idx, field, value) {
    const updated = [...lineItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setLineItems(updated);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/expenses/${expense._id}`,
        { lineItems, notes },
        {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
        },
      );
      setEditing(false);
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || t("wellDetail.expense.updateFailed"));
    } finally {
      setSaving(false);
    }
  }

  // In QuickBooks already: the money can't change here — reverse it.
  const exported = !!expense.qbExportedAt;
  const mayChange = canChangeExpense(user, expense);
  async function handleReverse() {
    const reason = window.prompt(t("wellDetail.expense.reversePrompt"), "");
    if (!reason || !reason.trim()) return;
    setError("");
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/expenses/${expense._id}/reverse`, { reason: reason.trim() }, { headers: { Authorization: "Bearer " + token } });
      onUpdated();
    } catch (err) {
      setError(err.response?.data?.error || t("wellDetail.expense.reverseFailed"));
    }
  }

  async function handleDelete() {
    if (!window.confirm(t("wellDetail.expense.confirmDelete")))
      return;
    setError("");
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/expenses/${expense._id}`, {
        headers: { Authorization: "Bearer " + token },
      });
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || t("wellDetail.expense.deleteFailed"));
    }
  }

  return (
    <div
      style={{
        backgroundColor: card,
        border: `1px solid ${border}`,
        borderRadius: "10px",
        padding: "16px",
        marginBottom: "12px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "12px",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "12px" }}>
          {new Date(expense.date).toLocaleDateString(locale, {
            weekday: isMobile ? "short" : "long",
            month: "short",
            day: "numeric",
            year: "numeric",
          })}
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <span style={{ color: cyan, fontSize: "18px", fontWeight: "800" }}>
            $
            {(editing ? total : expense.total).toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
          {exported && (
            <span data-testid="expense-in-qb" style={{ color: "#ffaa00", fontSize: "10px", border: "1px solid rgba(255,170,0,0.5)", borderRadius: "4px", padding: "3px 8px", letterSpacing: "1px" }}>
              {t("wellDetail.expense.inQuickBooks")}
            </span>
          )}
          {expense.reversalOf && <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "10px", letterSpacing: "1px" }}>{t("wellDetail.expense.isReversal")}</span>}
          {expense.reversedBy && <span style={{ color: "rgba(255,255,255,0.6)", fontSize: "10px", letterSpacing: "1px" }}>{t("wellDetail.expense.reversed")}</span>}
          {mayChange && exported && !expense.reversedBy && !expense.reversalOf && (
            <button
              type="button"
              onClick={handleReverse}
              style={{ background: "none", border: "1px solid rgba(255,170,0,0.5)", color: "#ffaa00", fontSize: "10px", padding: "4px 10px", borderRadius: "4px", cursor: "pointer", letterSpacing: "1px" }}
            >
              {t("wellDetail.expense.reverse")}
            </button>
          )}
          {mayChange && !exported && (
          <button
            onClick={() => setEditing(!editing)}
            style={{
              background: "none",
              border: `1px solid ${border}`,
              color: "rgba(0,245,255,0.6)",
              fontSize: "10px",
              padding: "4px 10px",
              borderRadius: "4px",
              cursor: "pointer",
              letterSpacing: "1px",
            }}
          >
            {editing ? t("wellDetail.cancel") : t("wellDetail.edit")}
          </button>
          )}
          {mayChange && !exported && (
          <button
            onClick={handleDelete}
            style={{
              background: "none",
              border: "1px solid rgba(255,68,102,0.4)",
              color: "rgba(255,68,102,0.7)",
              fontSize: "10px",
              padding: "4px 10px",
              borderRadius: "4px",
              cursor: "pointer",
              letterSpacing: "1px",
            }}
          >
            {t("wellDetail.delete")}
          </button>
          )}
        </div>
      </div>

      {!editing && error && (
        <div style={{ marginBottom: "10px" }}>
          <ErrorBanner message={error} />
        </div>
      )}

      {editing ? (
        <div>
          {isMobile ? (
            lineItems.map((item, idx) => (
              <div
                key={idx}
                style={{
                  backgroundColor: "rgba(0,245,255,0.04)",
                  borderRadius: "6px",
                  padding: "10px",
                  marginBottom: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "6px",
                  }}
                >
                  <span
                    style={{
                      color: cyan,
                      fontSize: "10px",
                      letterSpacing: "1px",
                    }}
                  >
                    {t("wellDetail.expense.item", { n: idx + 1 })}
                  </span>
                  <button
                    onClick={() => removeItem(idx)}
                    style={{
                      background: "none",
                      border: "1px solid rgba(255,68,102,0.4)",
                      color: "rgba(255,68,102,0.7)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "11px",
                      padding: "2px 8px",
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "6px",
                  }}
                >
                  <input
                    value={item.description}
                    onChange={(e) =>
                      updateItem(idx, "description", e.target.value)
                    }
                    placeholder={t("wellDetail.expense.description")}
                    style={inputStyle}
                  />
                  <input
                    value={item.vendor}
                    onChange={(e) => updateItem(idx, "vendor", e.target.value)}
                    placeholder={t("wellDetail.expense.vendor")}
                    style={inputStyle}
                  />
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "6px",
                    }}
                  >
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) =>
                        updateItem(idx, "amount", e.target.value)
                      }
                      placeholder={t("wellDetail.expense.amount")}
                      style={inputStyle}
                    />
                    <input
                      value={item.codeId}
                      onChange={(e) =>
                        updateItem(idx, "codeId", e.target.value)
                      }
                      placeholder={t("wellDetail.expense.codeId")}
                      style={inputStyle}
                    />
                  </div>
                  <input
                    list={`edit-cat-m-${expense._id}-${idx}`}
                    value={item.category}
                    onChange={(e) =>
                      updateItem(idx, "category", e.target.value)
                    }
                    placeholder={t("wellDetail.expense.category")}
                    style={inputStyle}
                  />
                  <datalist id={`edit-cat-m-${expense._id}-${idx}`}>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                  <input
                    value={item.notes}
                    onChange={(e) => updateItem(idx, "notes", e.target.value)}
                    placeholder={t("wellDetail.expense.notesOptional")}
                    style={{
                      ...inputStyle,
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.4)",
                    }}
                  />
                </div>
              </div>
            ))
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 28px",
                  gap: "6px",
                  marginBottom: "6px",
                  padding: "4px 6px",
                  backgroundColor: "rgba(0,245,255,0.06)",
                  borderRadius: "4px",
                }}
              >
                {[
                  t("wellDetail.expense.colDescription"),
                  t("wellDetail.expense.colVendor"),
                  t("wellDetail.expense.colAmount"),
                  t("wellDetail.expense.colCategory"),
                  t("wellDetail.expense.colCodeId"),
                  "",
                ].map((h, hi) => (
                  <div
                    key={hi}
                    style={{
                      color: cyan,
                      fontSize: "8px",
                      letterSpacing: "1px",
                      fontWeight: "700",
                    }}
                  >
                    {h}
                  </div>
                ))}
              </div>
              {lineItems.map((item, idx) => (
                <div key={idx} style={{ marginBottom: "6px" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 28px",
                      gap: "6px",
                      marginBottom: "4px",
                      alignItems: "center",
                    }}
                  >
                    <input
                      value={item.description}
                      onChange={(e) =>
                        updateItem(idx, "description", e.target.value)
                      }
                      placeholder={t("wellDetail.expense.description")}
                      style={inputStyle}
                    />
                    <input
                      value={item.vendor}
                      onChange={(e) =>
                        updateItem(idx, "vendor", e.target.value)
                      }
                      placeholder={t("wellDetail.expense.vendor")}
                      style={inputStyle}
                    />
                    <input
                      type="number"
                      value={item.amount}
                      onChange={(e) =>
                        updateItem(idx, "amount", e.target.value)
                      }
                      placeholder="0.00"
                      style={inputStyle}
                    />
                    <input
                      list={`edit-cat-${expense._id}-${idx}`}
                      value={item.category}
                      onChange={(e) =>
                        updateItem(idx, "category", e.target.value)
                      }
                      placeholder={t("wellDetail.expense.category")}
                      style={inputStyle}
                    />
                    <datalist id={`edit-cat-${expense._id}-${idx}`}>
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c} />
                      ))}
                    </datalist>
                    <input
                      value={item.codeId}
                      onChange={(e) =>
                        updateItem(idx, "codeId", e.target.value)
                      }
                      placeholder={t("wellDetail.expense.code")}
                      style={inputStyle}
                    />
                    <button
                      onClick={() => removeItem(idx)}
                      style={{
                        background: "none",
                        border: "1px solid rgba(255,68,102,0.4)",
                        color: "rgba(255,68,102,0.7)",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "11px",
                        padding: "3px 5px",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <input
                    value={item.notes}
                    onChange={(e) => updateItem(idx, "notes", e.target.value)}
                    placeholder={t("wellDetail.expense.notesOptional")}
                    style={{
                      ...inputStyle,
                      fontSize: "11px",
                      color: "rgba(255,255,255,0.4)",
                    }}
                  />
                </div>
              ))}
            </>
          )}
          <button
            onClick={addItem}
            style={{
              background: "none",
              border: "1px dashed rgba(0,245,255,0.3)",
              color: "rgba(0,245,255,0.5)",
              fontSize: "10px",
              padding: "6px 14px",
              borderRadius: "4px",
              cursor: "pointer",
              letterSpacing: "1px",
              width: "100%",
              marginTop: "6px",
              marginBottom: "12px",
            }}
          >
            {t("wellDetail.expense.addLineItem")}
          </button>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder={t("wellDetail.expense.reportNotes")}
            style={{ ...inputStyle, resize: "vertical", marginBottom: "10px" }}
          />
          {error && (
            <div style={{ marginBottom: "10px" }}>
              <ErrorBanner message={error} onRetry={handleSave} />
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "8px",
            }}
          >
            <span style={{ color: cyan, fontSize: "16px", fontWeight: "700" }}>
              {t("wellDetail.expense.total", {
                amount: total.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }),
              })}
            </span>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                color: "#020817",
                fontWeight: "700",
                fontSize: "11px",
                letterSpacing: "2px",
                padding: "8px 20px",
                borderRadius: "4px",
                border: "none",
                cursor: "pointer",
              }}
            >
              {saving ? t("wellDetail.saving") : t("wellDetail.saveChanges")}
            </button>
          </div>
        </div>
      ) : (
        <div>
          {expense.lineItems && expense.lineItems.length > 0 ? (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              {expense.lineItems.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 1fr"
                      : "2fr 1fr 1fr 1fr",
                    gap: "8px",
                    padding: "8px",
                    backgroundColor: "rgba(0,245,255,0.04)",
                    borderRadius: "4px",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "white",
                        fontSize: "12px",
                        fontWeight: "600",
                      }}
                    >
                      {item.description}
                    </div>
                    {item.vendor && (
                      <div
                        style={{
                          color: "rgba(255,255,255,0.3)",
                          fontSize: "10px",
                        }}
                      >
                        {item.vendor}
                      </div>
                    )}
                  </div>
                  {!isMobile && (
                    <div
                      style={{
                        color: "rgba(255,255,255,0.5)",
                        fontSize: "11px",
                      }}
                    >
                      {item.category || "—"}
                    </div>
                  )}
                  {!isMobile && (
                    <div
                      style={{
                        color: "rgba(255,255,255,0.4)",
                        fontSize: "10px",
                      }}
                    >
                      {item.codeId || "—"}
                    </div>
                  )}
                  <div
                    style={{
                      color: cyan,
                      fontSize: "13px",
                      fontWeight: "700",
                      textAlign: "right",
                    }}
                  >
                    $
                    {Number(item.amount).toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
                gap: "8px",
              }}
            >
              {expense.rig_day_rate > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(0,245,255,0.4)",
                      fontSize: "9px",
                      letterSpacing: "2px",
                    }}
                  >
                    {t("wellDetail.legacy.rigRate")}
                  </div>
                  <div
                    style={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    ${expense.rig_day_rate.toLocaleString()}
                  </div>
                </div>
              )}
              {expense.crew_labor > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(0,245,255,0.4)",
                      fontSize: "9px",
                      letterSpacing: "2px",
                    }}
                  >
                    {t("wellDetail.legacy.labor")}
                  </div>
                  <div
                    style={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    ${expense.crew_labor.toLocaleString()}
                  </div>
                </div>
              )}
              {expense.fuel_diesel > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(0,245,255,0.4)",
                      fontSize: "9px",
                      letterSpacing: "2px",
                    }}
                  >
                    {t("wellDetail.legacy.fuel")}
                  </div>
                  <div
                    style={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    ${expense.fuel_diesel.toLocaleString()}
                  </div>
                </div>
              )}
              {expense.equipment_rental > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(0,245,255,0.4)",
                      fontSize: "9px",
                      letterSpacing: "2px",
                    }}
                  >
                    {t("wellDetail.legacy.equipment")}
                  </div>
                  <div
                    style={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    ${expense.equipment_rental.toLocaleString()}
                  </div>
                </div>
              )}
              {expense.chemicals_mud > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(0,245,255,0.4)",
                      fontSize: "9px",
                      letterSpacing: "2px",
                    }}
                  >
                    {t("wellDetail.legacy.chemicals")}
                  </div>
                  <div
                    style={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    ${expense.chemicals_mud.toLocaleString()}
                  </div>
                </div>
              )}
              {expense.transportation > 0 && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  <div
                    style={{
                      color: "rgba(0,245,255,0.4)",
                      fontSize: "9px",
                      letterSpacing: "2px",
                    }}
                  >
                    {t("wellDetail.legacy.transport")}
                  </div>
                  <div
                    style={{
                      color: "white",
                      fontSize: "13px",
                      fontWeight: "700",
                    }}
                  >
                    ${expense.transportation.toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          )}
          {expense.notes && (
            <p
              style={{
                color: "rgba(255,255,255,0.4)",
                fontSize: "12px",
                marginTop: "10px",
                fontStyle: "italic",
              }}
            >
              {expense.notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Matches the existing well-editing permission tier (see routes/wells.js):
// admin + supervisor can create/update wells, admin only can delete them.
const WELL_WRITE_ROLES = ["admin", "supervisor"];
const WELL_DELETE_ROLES = ["admin"];
// Matches routes/tanks.js's canEdit tier exactly — tank setup is
// field-crew data entry (a pumper walking the battery with a tape
// measure), not office-only like well editing above.
const TANK_WRITE_ROLES = ["admin", "supervisor", "field_hand"];

// ─── MAIN WELLDETAIL COMPONENT ────────────────────────────
export default function WellDetail({ well, token, user, onBack, onNavigateToLease, initialTab }) {
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  // ─── ALL STATE INSIDE THE COMPONENT ──────────────────
  const [expenses, setExpenses] = useState([]);
  const [reports, setReports] = useState([]);
  const [reportsPage, setReportsPage] = useState(1);
  const [reportsTotal, setReportsTotal] = useState(0);
  const [reportsTotalPages, setReportsTotalPages] = useState(1);
  const [summary, setSummary] = useState({ sevenDayTotal: 0, latest: null });
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  const [loading, setLoading] = useState(true);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportStart, setExportStart] = useState("");
  const [exportEnd, setExportEnd] = useState("");
  const [showComplianceExport, setShowComplianceExport] = useState(false);
  const [complianceStart, setComplianceStart] = useState("");
  const [complianceEnd, setComplianceEnd] = useState("");
  const [todayJsa, setTodayJsa] = useState(null);
  const [history, setHistory] = useState([]);
  const [showJsaModal, setShowJsaModal] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [jsaMode, setJsaMode] = useState("create");
  const [selectedHistoryJsa, setSelectedHistoryJsa] = useState(null);
  const [showProcedureAnalyzer, setShowProcedureAnalyzer] = useState(false);
  const [error, setError] = useState("");

  const wellId = well._id || well.id;
  const headers = { Authorization: "Bearer " + token };
  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";
  const card = "rgba(0,245,255,0.03)";
  const statusColor = {
    active: "#00f5ff",
    standby: "#ffaa00",
    completed: "#4466aa",
    "shut-in": "#ff4466",
  };

  useEffect(() => {
    if (!wellId) return;
    getTodayJsa(wellId, token)
      .then(setTodayJsa)
      .catch(() => setTodayJsa(null));
    getJsaHistory(wellId, token)
      .then(setHistory)
      .catch(() => setHistory([]));
  }, [wellId, token]);

  // Small lease-status indicator, only fetched if this well actually has a
  // lease linked (see leaseId on the Well model) — 404/403 here just means
  // no lease is linked or the viewer's role can't see leases, so it's
  // swallowed rather than surfaced as a page error.
  const [wellLease, setWellLease] = useState(null);
  useEffect(() => {
    if (!well.leaseId) {
      setWellLease(null);
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/leases/${well.leaseId}`, { headers })
      .then((res) => setWellLease(res.data))
      .catch(() => setWellLease(null));
    // eslint-disable-next-line
  }, [well.leaseId, token]);

  const handleJsaSaved = (updated) => {
    setTodayJsa(updated);
    getJsaHistory(wellId, token)
      .then(setHistory)
      .catch(() => {});
  };

  async function fetchData(reportsPageArg = 1) {
    setLoading(true);
    try {
      const [expSummary, allReports] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/expenses/well/${wellId}`, { headers }),
        axios.get(`${process.env.REACT_APP_API_URL}/api/reports`, {
          headers,
          params: { wellId, page: reportsPageArg, limit: 25 },
        }),
      ]);
      setSummary(expSummary.data);
      setExpenses(expSummary.data.expenses || []);
      setReports(allReports.data.data || []);
      setReportsPage(allReports.data.page);
      setReportsTotal(allReports.data.total);
      setReportsTotalPages(allReports.data.totalPages);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("wellDetail.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  function handleReportsPageChange(newPage) {
    fetchData(newPage);
  }

  useEffect(() => {
    if (!wellId) return;
    fetchData();
    // eslint-disable-next-line
  }, [wellId, token]);

  const chartData = expenses
    .map((e) => ({
      date: formatShortDate(e.date),
      total: parseFloat(e.total.toFixed(2)),
    }))
    .reverse();

  const tabs = [
    "overview",
    "expenses",
    "reports",
    "safety",
    "vendor",
    "intelligence",
    "schematic",
    "buildsheet",
    "documents",
    "tanks",
  ].filter((tab) => tab !== "tanks" || showsTankGauging(user));
  // Display label per tab key (wellDetail.tabs.*), EN + ES. Always the
  // full label — on a phone the row scrolls (components/ScrollTabs).
  const tabLabel = (tab) => t(`wellDetail.tabs.${tab}`);

  const exportUrl = (format) =>
    `${process.env.REACT_APP_API_URL}/api/expenses/export?wellId=${wellId}&startDate=${exportStart}&endDate=${exportEnd}&format=${format}`;

  const complianceSummaryUrl = () =>
    `${process.env.REACT_APP_API_URL}/api/wells/${wellId}/compliance-summary?startDate=${complianceStart}&endDate=${complianceEnd}`;

  async function downloadExport(url, filename) {
    setError("");
    try {
      const res = await fetch(url, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error || t("wellDetail.exportFailed"));
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      setError(err.message || t("wellDetail.exportFailed"));
    }
  }

  // ─── PAGE RENDERS ─────────────────────────────────────
  if (showProcedureAnalyzer) {
    return (
      <Suspense fallback={<PageFallback />}>
        <ProcedureAnalyzer
          token={token}
          onBack={() => setShowProcedureAnalyzer(false)}
          wellName={well.well_name}
        />
      </Suspense>
    );
  }

  if (showExpenseForm) {
    return (
      <NewExpense
        token={token}
        wells={[well]}
        defaultWellId={wellId}
        onBack={() => setShowExpenseForm(false)}
        onSaved={() => {
          setShowExpenseForm(false);
          fetchData();
        }}
      />
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020817",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* NAV */}
      <nav
        style={{
          backgroundColor: "rgba(0,0,0,0.6)",
          borderBottom: `1px solid ${border}`,
          padding: isMobile ? "12px 16px" : "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: isMobile ? "18px" : "22px",
            fontWeight: "900",
            letterSpacing: "4px",
            background: "linear-gradient(135deg, #00f5ff, #0080ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          RIGIQ
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          <button
            onClick={() => setShowProcedureAnalyzer(true)}
            style={{
              background: "linear-gradient(135deg, #00f5ff, #0080ff)",
              color: "#020817",
              fontWeight: "700",
              fontSize: "10px",
              letterSpacing: "2px",
              padding: isMobile ? "0 14px" : "5px 14px",
              minHeight: isMobile ? "44px" : undefined,
              borderRadius: "4px",
              border: "none",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {t("wellDetail.analyze")}
          </button>
          {user?.role === "admin" && (
            <button
              type="button"
              data-testid="well-delete"
              onClick={async () => {
                if (!window.confirm(t("dashboard.main.confirmDeleteWell"))) return;
                try {
                  await axios.delete(`${process.env.REACT_APP_API_URL}/api/wells/${well._id || well.id}`, { headers: { Authorization: "Bearer " + token } });
                  onBack();
                } catch (err) {
                  window.alert(err.response?.data?.error || t("wellDetail.deleteFailed"));
                }
              }}
              style={{ color: "#ff6680", background: "none", border: "1px solid rgba(255,68,102,0.5)", cursor: "pointer", fontSize: "11px", padding: isMobile ? "0 14px" : "5px 12px", minHeight: isMobile ? "44px" : undefined, borderRadius: "4px", whiteSpace: "nowrap" }}
            >
              {t("wellDetail.deleteWell")}
            </button>
          )}
          <button
            onClick={onBack}
            style={{
              color: "rgba(0,245,255,0.5)",
              background: "none",
              border: `1px solid ${border}`,
              cursor: "pointer",
              fontSize: "11px",
              padding: isMobile ? "0 14px" : "5px 12px",
              minHeight: isMobile ? "44px" : undefined,
              borderRadius: "4px",
              letterSpacing: "1px",
              whiteSpace: "nowrap",
            }}
          >
            {t("wellDetail.back")}
          </button>
        </div>
      </nav>

      <main style={{ padding: isMobile ? "16px" : "28px" }}>
        {/* WELL HEADER */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "12px",
            padding: isMobile ? "16px" : "24px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "16px",
              gap: "12px",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <h2
                style={{
                  fontSize: isMobile ? "18px" : "24px",
                  fontWeight: "800",
                  color: "white",
                  marginBottom: "4px",
                  letterSpacing: "2px",
                }}
              >
                {well.well_name}
              </h2>
              <p
                style={{
                  color: "rgba(0,245,255,0.5)",
                  fontSize: "11px",
                  letterSpacing: isMobile ? "1px" : "3px",
                }}
              >
                {well.basin?.toUpperCase()} · {well.location?.toUpperCase()}
              </p>
            </div>
            <span
              style={{
                backgroundColor: statusColor[well.status] + "22",
                color: statusColor[well.status],
                fontSize: "10px",
                fontWeight: "700",
                padding: "5px 10px",
                borderRadius: "20px",
                border: `1px solid ${statusColor[well.status]}`,
                letterSpacing: "1px",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              ● {t(`wells.status.${well.status}`, { defaultValue: well.status?.toUpperCase() })}
            </span>
            {wellLease && (
              <span
                onClick={() => onNavigateToLease && onNavigateToLease(wellLease)}
                title={t("wellDetail.viewLease")}
                style={{
                  backgroundColor: LEASE_BADGE_COLOR[wellLease.riskStatus] + "22",
                  color:
                    wellLease.status !== "active"
                      ? "rgba(255,255,255,0.4)"
                      : LEASE_BADGE_COLOR[wellLease.riskStatus],
                  fontSize: "10px",
                  fontWeight: "700",
                  padding: "5px 10px",
                  borderRadius: "20px",
                  border: `1px solid ${
                    wellLease.status !== "active"
                      ? "rgba(255,255,255,0.4)"
                      : LEASE_BADGE_COLOR[wellLease.riskStatus]
                  }`,
                  letterSpacing: "1px",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                  cursor: onNavigateToLease ? "pointer" : "default",
                }}
              >
                📜{" "}
                {wellLease.status !== "active"
                  ? t(`wellDetail.leaseStatus.${wellLease.status}`, { defaultValue: wellLease.status.toUpperCase() })
                  : t(`wellDetail.leaseRisk.${wellLease.riskStatus}`, { defaultValue: LEASE_BADGE_LABEL[wellLease.riskStatus] })}
              </span>
            )}
          </div>

          {WELL_WRITE_ROLES.includes(user?.role) && (
            <div style={{ marginBottom: "16px" }}>
              <button
                onClick={() => setShowComplianceExport(!showComplianceExport)}
                style={{
                  background: "none",
                  border: `1px solid ${border}`,
                  color: "rgba(0,245,255,0.6)",
                  fontWeight: "700",
                  fontSize: "10px",
                  letterSpacing: "1px",
                  padding: "6px 10px",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                {t("wellDetail.compliance.button")}
              </button>

              {showComplianceExport && (
                <div
                  style={{
                    backgroundColor: "rgba(0,245,255,0.05)",
                    border: `1px solid ${border}`,
                    borderRadius: "8px",
                    padding: "16px",
                    marginTop: "10px",
                  }}
                >
                  <div
                    style={{
                      color: cyan,
                      fontSize: "10px",
                      fontWeight: "700",
                      letterSpacing: "3px",
                      marginBottom: "12px",
                    }}
                  >
                    {t("wellDetail.compliance.dateRange")}
                  </div>
                  <div
                    style={{
                      color: "rgba(255,170,0,0.85)",
                      fontSize: "10px",
                      lineHeight: "1.5",
                      marginBottom: "12px",
                    }}
                  >
                    {t("wellDetail.compliance.disclaimer")}
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile
                        ? "1fr 1fr"
                        : "1fr 1fr auto",
                      gap: "10px",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <label
                        style={{
                          color: "rgba(0,245,255,0.5)",
                          fontSize: "9px",
                          letterSpacing: "2px",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        {t("wellDetail.from")}
                      </label>
                      <input
                        type="date"
                        value={complianceStart}
                        onChange={(e) => setComplianceStart(e.target.value)}
                        style={{
                          width: "100%",
                          backgroundColor: "rgba(0,245,255,0.05)",
                          border: `1px solid rgba(0,245,255,0.2)`,
                          borderRadius: "6px",
                          padding: "8px 12px",
                          color: "white",
                          fontSize: "13px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <div>
                      <label
                        style={{
                          color: "rgba(0,245,255,0.5)",
                          fontSize: "9px",
                          letterSpacing: "2px",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        {t("wellDetail.to")}
                      </label>
                      <input
                        type="date"
                        value={complianceEnd}
                        onChange={(e) => setComplianceEnd(e.target.value)}
                        style={{
                          width: "100%",
                          backgroundColor: "rgba(0,245,255,0.05)",
                          border: `1px solid rgba(0,245,255,0.2)`,
                          borderRadius: "6px",
                          padding: "8px 12px",
                          color: "white",
                          fontSize: "13px",
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                    </div>
                    <button
                      onClick={() =>
                        downloadExport(
                          complianceSummaryUrl(),
                          `Compliance_Summary_${well.well_name}.pdf`,
                        )
                      }
                      style={{
                        background:
                          "linear-gradient(135deg, #00f5ff, #0080ff)",
                        color: "#020817",
                        fontWeight: "700",
                        fontSize: "10px",
                        letterSpacing: "2px",
                        padding: "8px 14px",
                        borderRadius: "4px",
                        border: "none",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t("wellDetail.compliance.downloadPdf")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* STATS */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
              gap: "10px",
              marginBottom: "16px",
            }}
          >
            {[
              {
                label: t("wellDetail.stats.phase"),
                value: wellPhaseLabel(t, well),
              },
              {
                label: t("wellDetail.stats.reports7d"),
                value: reports.filter(
                  (r) =>
                    new Date(r.date) >= new Date(Date.now() - 7 * 86400000),
                ).length,
              },
              {
                label: t("wellDetail.stats.cost7d"),
                value:
                  "$" +
                  summary.sevenDayTotal.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }),
              },
              {
                label: t("wellDetail.stats.latestCost"),
                value: summary.latest
                  ? "$" +
                    summary.latest.total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })
                  : t("wellDetail.na"),
              },
            ].map((stat) => (
              <div
                key={stat.label}
                style={{
                  backgroundColor: "rgba(0,245,255,0.05)",
                  borderRadius: "8px",
                  padding: isMobile ? "10px 12px" : "12px 16px",
                }}
              >
                <div
                  style={{
                    color: "rgba(0,245,255,0.4)",
                    fontSize: "9px",
                    letterSpacing: "2px",
                    marginBottom: "4px",
                  }}
                >
                  {stat.label}
                </div>
                <div
                  style={{
                    color: "white",
                    fontSize: isMobile ? "14px" : "16px",
                    fontWeight: "700",
                  }}
                >
                  {stat.value}
                </div>
              </div>
            ))}
          </div>

          <WeatherWidget
            lat={well.latitude}
            lon={well.longitude}
            token={token}
          />

          <JsaSummary
            jsa={todayJsa}
            // Consultants read JSAs; writers start / edit them (server: routes/jsaRoutes.js).
            onStart={
              canWriteJsa(user)
                ? () => {
                    setSelectedHistoryJsa(null);
                    setJsaMode("create");
                    setShowJsaModal(true);
                  }
                : undefined
            }
            onEdit={
              canWriteJsa(user)
                ? () => {
                    setSelectedHistoryJsa(null);
                    setJsaMode("edit");
                    setShowJsaModal(true);
                  }
                : undefined
            }
            onView={() => {
              setSelectedHistoryJsa(null);
              setJsaMode("view");
              setShowJsaModal(true);
            }}
            onHistory={() => setShowHistory(true)}
          />
        </div>

        {/* TABS — full labels; one scrollable row on a phone */}
        <ScrollTabs
          tabs={tabs.map((tab) => ({ key: tab, label: tabLabel(tab), icon: isMobile ? TAB_ICONS[tab] : undefined }))}
          active={activeTab}
          onChange={setActiveTab}
          isMobile={isMobile}
          ariaLabel={t("wellDetail.tabsAria")}
          testId="well-tabs"
          style={{ marginBottom: "20px" }}
        />

        {error && (
          <div style={{ marginBottom: "16px" }}>
            <ErrorBanner message={error} onRetry={fetchData} />
          </div>
        )}
        {loading && (
          <div style={{ marginBottom: "16px" }}>
            <LoadingLine label={t("wellDetail.loading")} />
          </div>
        )}

        {/* OVERVIEW TAB */}
        {!loading && activeTab === "overview" && (
          <div>
            <h3
              style={{
                color: cyan,
                marginBottom: "12px",
                fontSize: "13px",
                letterSpacing: "3px",
              }}
            >
              {t("wellDetail.overview.expenses7d")}
            </h3>
            <div
              style={{
                backgroundColor: card,
                border: `1px solid ${border}`,
                borderRadius: "12px",
                padding: "20px",
                height: isMobile ? "220px" : "300px",
              }}
            >
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={border} />
                    <XAxis
                      dataKey="date"
                      stroke={cyan}
                      tick={{ fontSize: isMobile ? 9 : 11 }}
                    />
                    <YAxis
                      stroke={cyan}
                      tick={{ fontSize: isMobile ? 9 : 11 }}
                      width={isMobile ? 50 : 60}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#020817",
                        border: "1px solid rgba(0,245,255,0.3)",
                        borderRadius: "6px",
                      }}
                      labelStyle={{ color: cyan }}
                      itemStyle={{ color: "white" }}
                      formatter={(value) => [
                        "$" + value.toLocaleString(),
                        t("wellDetail.overview.chartTotal"),
                      ]}
                    />
                    <Bar dataKey="total" fill="#00f5ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "rgba(0,245,255,0.3)",
                    fontSize: "12px",
                    letterSpacing: "3px",
                  }}
                >
                  {t("wellDetail.overview.noExpenseData")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* EXPENSES TAB */}
        {!loading && activeTab === "expenses" && (
          <div>
            <AfeCard wellId={wellId} token={token} user={user} />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "12px",
                flexWrap: "wrap",
                gap: "8px",
              }}
            >
              <h3
                style={{ color: cyan, fontSize: "13px", letterSpacing: "3px" }}
              >
                {t("wellDetail.expenses.history")}
              </h3>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  onClick={() => setShowExport(!showExport)}
                  style={{
                    background: "none",
                    border: `1px solid ${border}`,
                    color: "rgba(0,245,255,0.6)",
                    fontWeight: "700",
                    fontSize: "10px",
                    letterSpacing: "1px",
                    padding: "6px 10px",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {t("wellDetail.expenses.export")}
                </button>
                <button
                  onClick={() => setShowExpenseForm(true)}
                  style={{
                    background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                    color: "#020817",
                    fontWeight: "700",
                    fontSize: "10px",
                    letterSpacing: "1px",
                    padding: "6px 12px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  {t("wellDetail.expenses.add")}
                </button>
              </div>
            </div>

            {showExport && (
              <div
                style={{
                  backgroundColor: "rgba(0,245,255,0.05)",
                  border: `1px solid ${border}`,
                  borderRadius: "8px",
                  padding: "16px",
                  marginBottom: "16px",
                }}
              >
                <div
                  style={{
                    color: cyan,
                    fontSize: "10px",
                    fontWeight: "700",
                    letterSpacing: "3px",
                    marginBottom: "12px",
                  }}
                >
                  {t("wellDetail.expenses.exportRange")}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile
                      ? "1fr 1fr"
                      : "1fr 1fr auto auto",
                    gap: "10px",
                    alignItems: "end",
                  }}
                >
                  <div>
                    <label
                      style={{
                        color: "rgba(0,245,255,0.5)",
                        fontSize: "9px",
                        letterSpacing: "2px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      {t("wellDetail.from")}
                    </label>
                    <input
                      type="date"
                      value={exportStart}
                      onChange={(e) => setExportStart(e.target.value)}
                      style={{
                        width: "100%",
                        backgroundColor: "rgba(0,245,255,0.05)",
                        border: `1px solid rgba(0,245,255,0.2)`,
                        borderRadius: "6px",
                        padding: "8px 12px",
                        color: "white",
                        fontSize: "13px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <div>
                    <label
                      style={{
                        color: "rgba(0,245,255,0.5)",
                        fontSize: "9px",
                        letterSpacing: "2px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      {t("wellDetail.to")}
                    </label>
                    <input
                      type="date"
                      value={exportEnd}
                      onChange={(e) => setExportEnd(e.target.value)}
                      style={{
                        width: "100%",
                        backgroundColor: "rgba(0,245,255,0.05)",
                        border: `1px solid rgba(0,245,255,0.2)`,
                        borderRadius: "6px",
                        padding: "8px 12px",
                        color: "white",
                        fontSize: "13px",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </div>
                  <button
                    onClick={() =>
                      downloadExport(
                        exportUrl("pdf"),
                        `expenses_${well.well_name}.pdf`,
                      )
                    }
                    style={{
                      background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                      color: "#020817",
                      fontWeight: "700",
                      fontSize: "10px",
                      letterSpacing: "2px",
                      padding: "8px 14px",
                      borderRadius: "4px",
                      border: "none",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("wellDetail.expenses.pdf")}
                  </button>
                  <button
                    onClick={() =>
                      downloadExport(
                        exportUrl("csv"),
                        `expenses_${well.well_name}.csv`,
                      )
                    }
                    style={{
                      background: "none",
                      border: `1px solid ${border}`,
                      color: "rgba(0,245,255,0.7)",
                      fontWeight: "700",
                      fontSize: "10px",
                      letterSpacing: "2px",
                      padding: "8px 14px",
                      borderRadius: "4px",
                      cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("wellDetail.expenses.csv")}
                  </button>
                </div>
                <div
                  style={{
                    color: "rgba(255,255,255,0.3)",
                    fontSize: "10px",
                    marginTop: "8px",
                  }}
                >
                  {t("wellDetail.expenses.exportHint")}
                </div>
              </div>
            )}

            {expenses.length > 0 && (
              <div
                style={{
                  backgroundColor: "rgba(0,245,255,0.05)",
                  border: `1px solid ${border}`,
                  borderRadius: "10px",
                  padding: "16px",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{
                    color: cyan,
                    fontSize: "10px",
                    fontWeight: "700",
                    letterSpacing: "3px",
                    marginBottom: "12px",
                  }}
                >
                  {t("wellDetail.expenses.costSummary")}
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                    gap: "8px",
                  }}
                >
                  {[
                    {
                      label: t("wellDetail.expenses.days7"),
                      value:
                        "$" +
                        expenses
                          .filter(
                            (e) =>
                              new Date(e.date) >=
                              new Date(Date.now() - 7 * 86400000),
                          )
                          .reduce((s, e) => s + e.total, 0)
                          .toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }),
                    },
                    {
                      label: t("wellDetail.expenses.days30"),
                      value:
                        "$" +
                        expenses
                          .filter(
                            (e) =>
                              new Date(e.date) >=
                              new Date(Date.now() - 30 * 86400000),
                          )
                          .reduce((s, e) => s + e.total, 0)
                          .toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }),
                    },
                    {
                      label: t("wellDetail.expenses.allTime"),
                      value:
                        "$" +
                        expenses
                          .reduce((s, e) => s + e.total, 0)
                          .toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }),
                    },
                  ].map((stat) => (
                    <div
                      key={stat.label}
                      style={{
                        backgroundColor: "rgba(0,245,255,0.05)",
                        borderRadius: "6px",
                        padding: "10px",
                      }}
                    >
                      <div
                        style={{
                          color: "rgba(0,245,255,0.4)",
                          fontSize: "9px",
                          letterSpacing: "2px",
                          marginBottom: "4px",
                        }}
                      >
                        {stat.label}
                      </div>
                      <div
                        style={{
                          color: cyan,
                          fontSize: isMobile ? "13px" : "16px",
                          fontWeight: "800",
                        }}
                      >
                        {stat.value}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {expenses.length === 0 ? (
              <div
                style={{
                  color: "rgba(0,245,255,0.3)",
                  fontSize: "12px",
                  letterSpacing: "3px",
                  textAlign: "center",
                  padding: "40px",
                }}
              >
                {t("wellDetail.expenses.none")}
              </div>
            ) : (
              expenses.map((e) => (
                <ExpenseCard
                  key={e._id}
                  expense={e}
                  user={user}
                  token={token}
                  onDeleted={fetchData}
                  onUpdated={fetchData}
                  isMobile={isMobile}
                />
              ))
            )}
          </div>
        )}

        {/* REPORTS TAB */}
        {!loading && activeTab === "reports" && (
          <div>
            <h3
              style={{
                color: cyan,
                marginBottom: "16px",
                fontSize: "13px",
                letterSpacing: "3px",
              }}
            >
              {t("wellDetail.reports.title")}
            </h3>
            {reportsTotal > 0 && (
              <p
                style={{
                  color: "rgba(0,245,255,0.4)",
                  fontSize: "11px",
                  letterSpacing: "1px",
                  marginBottom: "12px",
                }}
              >
                {t("wellDetail.reports.showing", { shown: reports.length, total: reportsTotal })}
              </p>
            )}
            {reports.length === 0 ? (
              <div
                style={{
                  color: "rgba(0,245,255,0.3)",
                  fontSize: "12px",
                  letterSpacing: "3px",
                  textAlign: "center",
                  padding: "40px",
                }}
              >
                {t("wellDetail.reports.none")}
              </div>
            ) : (
              reports.map((r) => (
                <div
                  key={r._id}
                  style={{
                    backgroundColor: card,
                    border: `1px solid ${border}`,
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "10px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "8px",
                      flexWrap: "wrap",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        color: cyan,
                        fontWeight: "700",
                        fontSize: "13px",
                      }}
                    >
                      {formatDate(r.date)}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          color: "rgba(255,255,255,0.3)",
                          fontSize: "11px",
                          letterSpacing: "1px",
                        }}
                      >
                        {r.format === "tour_sheet"
                          ? `${r.rigName} · ${t(`rigReport.status.${r.status}`)}`
                          : t("wellDetail.reports.hoursDrilling", { hours: r.hours_drilling })}
                      </span>
                      <button
                        onClick={() =>
                          r.format === "tour_sheet"
                            ? downloadFile(`${process.env.REACT_APP_API_URL}/api/reports/${r._id}/pdf`, token, "tour-sheet.pdf").catch(() => setError(t("rigReport.actionFailed")))
                            : exportReportPDF(r, well.well_name)
                        }
                        style={{
                          background: "none",
                          border: "1px solid rgba(0,245,255,0.3)",
                          color: "rgba(0,245,255,0.7)",
                          fontSize: "10px",
                          padding: "4px 10px",
                          borderRadius: "4px",
                          cursor: "pointer",
                          letterSpacing: "1px",
                        }}
                      >
                        {t("wellDetail.reports.downloadPdf")}
                      </button>
                    </div>
                  </div>
                  <p
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "13px",
                      marginBottom: "10px",
                      lineHeight: "1.6",
                    }}
                  >
                    {r.operations}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      fontSize: "11px",
                      color: "rgba(0,245,255,0.6)",
                      letterSpacing: "1px",
                    }}
                  >
                    {r.format === "tour_sheet" ? (
                      r.totals && (
                        <>
                          <span>{t("rigReport.cardHours.working", { h: r.totals.working })}</span>
                          {r.totals.standby > 0 && <span>{t("rigReport.cardHours.standby", { h: r.totals.standby })}</span>}
                          <span>{t("rigReport.cardHours.crew", { h: r.totals.crewHours })}</span>
                        </>
                      )
                    ) : (
                      <>
                        <span>📏 {r.footage} FT</span>
                        <span>⚖️ {r.mud_weight} PPG</span>
                        <span>🔩 {r.bit_depth} FT TD</span>
                      </>
                    )}
                  </div>
                </div>
              ))
            )}
            <Pagination
              page={reportsPage}
              limit={25}
              total={reportsTotal}
              totalPages={reportsTotalPages}
              onPageChange={handleReportsPageChange}
              itemLabel={t("wellDetail.reports.itemLabel")}
            />
          </div>
        )}

        {/* VENDOR TAB */}
        {!loading && activeTab === "vendor" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <VendorDashboard
              token={token}
              wells={[well]}
              user={{ role: "admin", name: "User" }}
              onBack={() => setActiveTab("overview")}
              filterWellId={wellId}
            />
          </Suspense>
        )}
        {/* INTELLIGENCE TAB */}
        {!loading && activeTab === "intelligence" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <WellIntelligence well={well} token={token} user={user} />
          </Suspense>
        )}

        {/* SCHEMATIC TAB */}
        {!loading && activeTab === "schematic" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <WellboreSchematic well={well} token={token} user={user} />
          </Suspense>
        )}

        {/* BUILD SHEET TAB */}
        {!loading && activeTab === "buildsheet" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <BuildSheet well={well} token={token} user={user} />
          </Suspense>
        )}

        {/* SAFETY TAB */}
        {!loading && activeTab === "safety" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <SafetyDashboard
              token={token}
              wells={[well]}
              user={{ role: "admin", name: "User" }}
              onBack={() => setActiveTab("overview")}
              filterWellId={wellId}
            />
          </Suspense>
        )}

        {/* DOCUMENTS TAB */}
        {!loading && activeTab === "documents" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <WellDocuments
              wellId={wellId}
              wellName={well.well_name}
              token={token}
              canWrite={WELL_WRITE_ROLES.includes(user?.role)}
              canDelete={WELL_DELETE_ROLES.includes(user?.role)}
              onBack={() => setActiveTab("overview")}
            />
          </Suspense>
        )}

        {/* TANKS TAB */}
        {!loading && activeTab === "tanks" && (
          <Suspense fallback={<LoadingLine label={t("wellDetail.loading")} />}>
            <WellTanks
              wellId={wellId}
              wellName={well.well_name}
              token={token}
              canWrite={TANK_WRITE_ROLES.includes(user?.role)}
              onBack={() => setActiveTab("overview")}
            />
          </Suspense>
        )}
      </main>

      {/* JSA MODAL */}
      {showJsaModal && (
        <JsaModal
          wellId={wellId}
          jsa={selectedHistoryJsa || todayJsa}
          mode={jsaMode}
          token={token}
          onClose={() => {
            setShowJsaModal(false);
            setSelectedHistoryJsa(null);
          }}
          onSaved={handleJsaSaved}
        />
      )}

      {/* JSA HISTORY DRAWER */}
      {showHistory && (
        <JsaHistoryDrawer
          history={history}
          onClose={() => setShowHistory(false)}
          onView={(item) => {
            setSelectedHistoryJsa(item);
            setJsaMode("view");
            setShowJsaModal(true);
            setShowHistory(false);
          }}
        />
      )}
    </div>
  );
}
